import blobStream from 'blob-stream'
// TODO fix import for Typescript support
import {TFunction} from 'i18next'
import PDFDocument from 'pdfkit/js/pdfkit.standalone'

export interface PdfData {
  date: string
  address: string
  titleText: string
  signatureText: string
  materialText: string[]
  job?: string
  contractor?: string
  masonry?: string
  architect?: string
  logoUrl: string
  signatureImageUrl?: string
  companyName?: string
}

// eslint-disable-next-line max-statements
export const createPdfDocument = async (
  callback: (arg: string) => void,
  {
    date,
    address,
    job,
    contractor,
    masonry,
    architect,
    titleText,
    signatureText,
    materialText,
    logoUrl,
    signatureImageUrl,
    companyName = 'Heidelberg Materials'
  }: PdfData,
  t: TFunction
): Promise<void> => {
  // Create a document
  const doc = new PDFDocument({
    autoFirstPage: false
  })

  // Prevent default page to be able to control layout of first page, too.
  // "Every printer is different but 0.25" (6.35 mm) is a safe bet." - https://stackoverflow.com/a/3513476
  doc.addPage({
    size: 'A4',
    layout: 'portrait',
    // 1/4 inch
    margin: 40
  })

  // pipe the document to a blob
  const stream = doc.pipe(blobStream())

  /**
   * Logo - positioned on the left
   */
  const response = await fetch(logoUrl)
  const imageBuffer = await response.arrayBuffer()
  doc.image(imageBuffer, 40, 30, {width: 110})

  /**
   * Company Name - positioned on the right in green, bold
   */
  doc.fontSize(18)
  doc.fillColor('#004E2B')
  doc.font('Helvetica-Bold')
  doc.text(companyName, 300, 30, {width: 255, align: 'right'})

  /**
   * Company Division - below logo area, black, bold
   */
  doc.fontSize(9)
  doc.fillColor('black')
  doc.font('Helvetica-Bold')
  doc.text('Heidelberg Materials North America', 300, 95, {width: 255, align: 'right'})

  /**
   * Title Text (plant address from snippets) - below division name
   */
  if (titleText) {
    doc.fontSize(8)
    doc.fillColor('#666666')
    doc.font('Helvetica')
    // Replace escaped newlines with actual newlines
    const formattedTitleText = titleText.replace(/\\n/g, '\n')
    doc.text(formattedTitleText, 300, 110, {width: 255, align: 'right'})
  }

  const baseTextProps = {width: 500}

  /**
   * Date - positioned below the header section
   */
  doc.fillColor('black')
  doc.fontSize(12)
  doc.font('Helvetica')
  doc.text(date, 40, 150, baseTextProps)

  /**
   * Address
   */

  doc.moveDown(3)
  doc.text(address, baseTextProps)

  /**
   * Optional Data
   */

  doc.moveDown(1.5)
  job && doc.text(`${t('certificate.pdf.job')}: ${job}`, baseTextProps)
  contractor && doc.text(`${t('certificate.pdf.contractor')}: ${contractor}`, baseTextProps)
  masonry && doc.text(`${t('certificate.pdf.masonry')}: ${masonry}`, baseTextProps)
  architect && doc.text(`${t('certificate.pdf.architect')}: ${architect}`, baseTextProps)

  /**
   * Main body
   */
  doc.moveDown(3)
  doc.font('Helvetica-Bold')
  doc.text(t('certificate.pdf.subject'), baseTextProps)
  doc.moveDown(3)
  doc.font('Helvetica')
  doc.text(`${t('certificate.pdf.dear')},`, baseTextProps)
  doc.moveDown(1)

  materialText.forEach((text) => {
    // Strip HTML tags and replace escaped newlines
    const cleanText = text
      .replace(/<[^>]*>/g, '')
      .replace(/\\n/g, '\n')
    doc.text(cleanText, baseTextProps)
    doc.moveDown(1)
  })

  /**
   * Closing and Signature
   */
  doc.moveDown(2)
  doc.text('Sincerely,', baseTextProps)

  /**
   * Signature Image (if provided)
   */
  if (signatureImageUrl) {
    try {
      const sigResponse = await fetch(signatureImageUrl)
      const sigBuffer = await sigResponse.arrayBuffer()
      doc.moveDown(1)
      doc.image(sigBuffer, 40, doc.y, {width: 120})
      doc.moveDown(3)
    } catch (error) {
      console.error('Failed to load signature image:', error)
      doc.moveDown(2)
    }
  } else {
    doc.moveDown(2)
  }

  /**
   * Signer Name and Title
   */
  const cleanSignatureText = signatureText
    .replace(/<[^>]*>/g, '')
    .replace(/\\n/g, '\n')
  doc.text(cleanSignatureText, baseTextProps)

  doc.end()

  stream.on('finish', () => callback(stream.toBlob('application/pdf')))
}

export const downloadBlob = (blob: unknown, name = 'file.txt'): void => {
  // Convert your blob into a Blob URL (a special url that points to an object in the browser's memory)
  const blobUrl = URL.createObjectURL(blob as Blob)
  if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
    // for IE
    ;(window.navigator as any).msSaveOrOpenBlob(blob, name)
  } else {
    window.open(blobUrl)
  }
}

export const isIE11 = !!/Trident\/7\./.exec(navigator.userAgent)
